import React from "react";
import { cn } from "@repo/ui/utils.ts";
import { PortableText, type PortableTextReactComponents } from "next-sanity";
import {
  asPortableText,
  hardBreakHandler,
  onMissingPortableTextComponent,
  proseBaseClasses,
} from "./shared";
import { AudioPlayer } from "@/components/common/audio-player/audio-player.component";

/**
 * Dedicated component for the `richTextCondensed` schema.
 *
 * Schema permits ONLY `normal` blocks and the `strong` decorator. The `as`
 * prop selects a typography preset. Heading-style modes (`heading`,
 * `heading-h6`, `heading-h5-h6`, `heading-display`) re-tag the normal block
 * as a semantic heading element so the same condensed content can act as a
 * section title where the design calls for it.
 *
 * Text colour is NOT bundled into the mode — pass `text-text-primary` /
 * `text-text-secondary` (or any other colour) via `className` at the call
 * site so the rich-text component stays context-agnostic.
 */
export type RichTextCondensedAs =
  // Body — regular weight
  | "body"
  | "body-tablet"
  | "body-lg"
  | "body-sm"
  | "body-xs-flat"
  // Body — medium weight
  | "body-medium"
  | "body-medium-tablet"
  | "body-md-medium-flat"
  | "body-sm-medium-flat"
  // Body — mixed / niche
  | "body-mixed-tablet"
  | "body-banner-headline"
  // Headings (re-tag the normal block as a semantic element)
  | "heading"
  | "heading-serif"
  | "heading-h6"
  | "heading-h5-h6"
  | "heading-display"
  | "subtitle";

const wrapperClasses: Record<RichTextCondensedAs, string> = {
  // Body — regular weight
  body: "typography-body-sm-regular desktop:typography-body-md-regular",
  "body-tablet": "typography-body-sm-regular tablet:typography-body-md-regular",
  "body-lg": "typography-body-md-regular desktop:typography-body-lg-regular",
  "body-sm": "typography-body-xs-regular desktop:typography-body-sm-regular",
  "body-xs-flat": "typography-body-xs-regular",
  // Body — medium weight
  "body-medium": "typography-body-sm-medium desktop:typography-body-md-medium",
  "body-medium-tablet":
    "typography-body-sm-medium tablet:typography-body-md-medium",
  "body-md-medium-flat": "typography-body-md-medium",
  "body-sm-medium-flat": "typography-body-sm-medium",
  // Body — mixed / niche
  "body-mixed-tablet":
    "typography-body-sm-medium tablet:typography-body-md-regular",
  "body-banner-headline":
    "typography-body-md-medium desktop-sm:typography-body-xl-medium",
  // Headings (typography applied directly on the heading element below)
  heading: "",
  "heading-serif": "",
  "heading-h6": "",
  "heading-h5-h6": "",
  "heading-display": "",
  subtitle:
    "typography-mobile-heading-subtitle-1 tablet:typography-desktop-heading-subtitle-1",
};

const HEADING_H5_CLASS =
  "typography-mobile-heading-h5 desktop:typography-desktop-heading-h5 m-0 text-inherit";
// Editorial statements / quotes (Season Mix, H3).
const HEADING_SERIF_CLASS =
  "typography-mobile-editorial-h3 desktop:typography-desktop-editorial-h3 m-0 text-inherit";
const HEADING_H6_CLASS =
  "typography-mobile-heading-h6 desktop:typography-desktop-heading-h6 m-0 text-inherit";
const HEADING_H5_H6_CLASS =
  "typography-mobile-heading-h5 desktop:typography-desktop-heading-h6 m-0 text-inherit";
const HEADING_DISPLAY_CLASS =
  "typography-mobile-heading-h1-voyage desktop:typography-desktop-heading-h1-voyage m-0 text-inherit";

function NormalAsH2H5(props: React.PropsWithChildren) {
  return <h2 className={HEADING_H5_CLASS}>{props.children}</h2>;
}
function NormalAsH2Serif(props: React.PropsWithChildren) {
  return <h2 className={HEADING_SERIF_CLASS}>{props.children}</h2>;
}
function NormalAsH2H6(props: React.PropsWithChildren) {
  return <h2 className={HEADING_H6_CLASS}>{props.children}</h2>;
}
function NormalAsH2H5H6(props: React.PropsWithChildren) {
  return <h2 className={HEADING_H5_H6_CLASS}>{props.children}</h2>;
}
function NormalAsH1(props: React.PropsWithChildren) {
  return <h1 className={HEADING_DISPLAY_CLASS}>{props.children}</h1>;
}

function getComponents(
  as: RichTextCondensedAs,
): Partial<PortableTextReactComponents> {
  const block: PortableTextReactComponents["block"] = {};
  if (as === "heading") block.normal = NormalAsH2H5;
  else if (as === "heading-serif") block.normal = NormalAsH2Serif;
  else if (as === "heading-h6") block.normal = NormalAsH2H6;
  else if (as === "heading-h5-h6") block.normal = NormalAsH2H5H6;
  else if (as === "heading-display") block.normal = NormalAsH1;

  return {
    block,
    types: {
      audioPlayer: ({ value }) => {
        const audio = value as { audioUrl?: string; title?: string };
        return audio.audioUrl ? (
          <AudioPlayer src={audio.audioUrl} title={audio.title} />
        ) : null;
      },
    },
    hardBreak: hardBreakHandler,
  };
}

interface RichTextCondensedProps<T = unknown> {
  richText?: T | null;
  className?: string;
  as?: RichTextCondensedAs;
  compact?: boolean;
}

export function RichTextCondensed<T = unknown>({
  richText,
  className,
  as = "body",
  compact,
}: RichTextCondensedProps<T>) {
  if (!richText) return null;
  return (
    <div
      className={cn(
        proseBaseClasses,
        wrapperClasses[as],
        compact && "[&>p]:m-0",
        className,
      )}
    >
      <PortableText
        value={asPortableText(richText)}
        components={getComponents(as)}
        onMissingComponent={onMissingPortableTextComponent}
      />
    </div>
  );
}
